// Work kit — issue URL-STATE controller (wave 4, Track A). Mounted on the
// issue body root; owns the ONE param it is allowed to write: `?selectedIssue=`
// (one-writer-per-param, replaceState-only — never a history entry, pin A6).
// The kit-wide map of every param and its single writer is the registry:
// docs/work/javascript-api.md, "URL-param ownership registry" (W8F). The
// board writer the registry reserves (`ui--work-board-url-state`) has never
// shipped — this controller's board suppression (A7) is its consumer.
//
//   - Boot replay (pin A5): on connect, if the URL already carries
//     `?selectedIssue=<this issue key>`, open the surrounding drawer and
//     focus the issue header — a refresh or a shared link restores the view.
//   - Drawer close (pin A6): when the surrounding drawer closes, the param is
//     removed via replaceState (fail-closed: the URL never advertises a
//     selection nothing is showing).
//   - Board suppression (pin A7): inside a W2 board shell
//     (`ui--work-board-url-state` ancestor) this controller is FULLY inert —
//     the board owns selection; two writers would fight.
//   - Turbo guard: the connect/replay work runs once per element via an
//     element-level dataset marker, so a Turbo morph that re-runs connect
//     cannot double-open the drawer (pin A8).
//
// The kit never navigates: opening the drawer reuses the existing
// drawer controller instance; focusing the header uses the body's
// `[data-work-issue-header]` anchor.

import { Controller } from "@hotwired/stimulus"

const PARAM = "selectedIssue"
const BOARD_URL_STATE_SELECTOR = "[data-controller~='ui--work-board-url-state']"
const DRAWER_SELECTOR = "[data-controller~='ui--drawer']"
const REPLAY_MARKER = "data-work-url-state-booted"

export default class extends Controller {
  static values = {
    issueKey: String,
  }

  connect() {
    // Turbo guard: ONE boot replay per element, ever — but the drawer:close
    // listener must re-bind on every connect, or a Turbo morph would leave
    // the param stuck in the URL (A8 pins both halves).
    const booted = this.element.hasAttribute(REPLAY_MARKER)
    this.element.setAttribute(REPLAY_MARKER, "true")

    this._inert = this._inBoardShell()
    this._onDrawerClose = this._handleDrawerClose.bind(this)
    window.addEventListener("drawer:close", this._onDrawerClose)

    if (booted || this._inert) return
    this._bootReplay()
  }

  disconnect() {
    window.removeEventListener("drawer:close", this._onDrawerClose)
  }

  // --- internals ---

  _inBoardShell() {
    return Boolean(this.element.closest(BOARD_URL_STATE_SELECTOR))
  }

  _currentParam() {
    return new URLSearchParams(window.location.search).get(PARAM)
  }

  // Boot replay: the URL says this issue should be open — restore it.
  _bootReplay() {
    if (this._currentParam() !== this.issueKeyValue) return
    const drawer = this.element.closest(DRAWER_SELECTOR)
    if (drawer) {
      const controller = this.application.getControllerForElementAndIdentifier(drawer, "ui--drawer")
      controller?.show()
    }
    // Focus the header anchor (explicit tabindex=-1) so screen readers and
    // keyboard users land IN the issue, not on the body.
    const header = this.element.querySelector("[data-work-issue-header]")
    requestAnimationFrame(() => header?.focus())
  }

  // Drawer close → drop the param. Only when this issue's drawer is the one
  // closing, and only in page shells we own (board shells never wrote it).
  _handleDrawerClose(event) {
    if (this._inert) return
    const drawer = event.target
    if (!(drawer instanceof Element)) return
    if (!drawer.matches(DRAWER_SELECTOR)) return
    if (!drawer.contains(this.element)) return
    if (this._currentParam() === null) return
    this._removeParam()
  }

  // replaceState ONLY — the back button must not step through selection
  // states (pin A6).
  _removeParam() {
    const url = new URL(window.location.href)
    url.searchParams.delete(PARAM)
    window.history.replaceState(window.history.state, "", url)
  }
}
